import { BadgeHelpIcon } from '@/components/BadgeKey';

// Stock Modal header — par / call-risk chip for preferreds, baby bonds and
// treasury-vehicle preferreds (admin-confirmed terms). Red when above par AND
// callable now (a call = instant loss), amber when above par with a future
// call or issuer's-choice redemption, quiet par note otherwise. Each state
// carries its Badge Key id so the ? icon lands on the matching row.
const BASE = 'inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wide border cursor-help';
const RED = `${BASE} font-bold bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300 border-red-400/60`;
const AMBER = `${BASE} font-bold bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border-amber-400/60`;
const MUTED = `${BASE} font-semibold bg-muted text-muted-foreground border-border`;

export default function PreferredCallChip({ stock }) {
  const par = Number(stock.par_value);
  const px = Number(stock.price);
  if (!par || par <= 0) return null;
  const rawCall = stock.preferred_call_date || null;
  const issuerChoice = rawCall === 'issuer-choice';
  const callIso = issuerChoice ? null : rawCall;
  const todayIso = new Date().toISOString().slice(0, 10);
  const callPast = !!callIso && callIso <= todayIso;
  const abovePar = px > 0 && px > par * 1.002;
  const parTxt = `$${par.toFixed(2)}`;

  let cls = MUTED;
  let badgeKey = 'pref_par';
  let title;
  let label;
  if (abovePar && callPast) {
    cls = RED; badgeKey = 'pref_callable_above_par';
    title = `Call risk: the issuer can redeem these shares at the ${parTxt} par value at any time (call protection ended ${callIso}). At $${px.toFixed(2)} you would take an instant loss of $${(px - par).toFixed(2)} per share if called.`;
    label = <>⚠ Callable at {parTxt} — above par</>;
  } else if (abovePar && issuerChoice) {
    cls = AMBER; badgeKey = 'pref_issuer_choice';
    title = `Trading above the ${parTxt} par value. The prospectus allows the issuer to redeem at par at any time on 15–60 business-days notice — no fixed call date — so a call would hand you an instant loss at today's price of $${(px - par).toFixed(2)} per share.`;
    label = <>⚠ Above {parTxt} par · callable at issuer&apos;s choice</>;
  } else if (abovePar && callIso) {
    cls = AMBER; badgeKey = 'pref_call_above_par';
    title = `Trading above the ${parTxt} par value. Call protection ends ${callIso} — after that the issuer can redeem at par, which would hand you a loss at today's price.`;
    label = <>Above {parTxt} par · callable {callIso}</>;
  } else {
    badgeKey = issuerChoice ? 'pref_issuer_choice' : callPast ? 'pref_callable_now' : 'pref_par';
    title = `Par value ${parTxt}${issuerChoice ? " · callable at any time on 15–60 business-days notice at the issuer's choice (no fixed call date in the prospectus)" : callIso ? ` · callable by the issuer ${callPast ? `now (since ${callIso})` : `from ${callIso}`}` : ''}. ${px > 0 && px < par ? 'Trading below par — a call would pay you the difference.' : ''}`;
    label = <>Par {parTxt}{issuerChoice ? " · callable at issuer's choice" : callPast ? ' · callable now' : ''}</>;
  }
  return (
    <span className="inline-flex items-center gap-1">
      <span className={cls} title={title} data-testid={badgeKey === 'pref_par' || badgeKey === 'pref_callable_now' || (badgeKey === 'pref_issuer_choice' && !abovePar) ? 'modal-preferred-par-chip' : 'modal-preferred-call-risk-chip'}>
        {label}
      </span>
      <BadgeHelpIcon badgeKey={badgeKey} testIdSuffix="modal" />
    </span>
  );
}
